import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '../../shared_components/UI/Box'

import {
  selectIsDarkThemeState,
} from '../../modules/miscUi/selectors'
import {
  setHeaderBgUrl,
  setShowHeaderGradient,
} from '../../modules/miscUi/actions'

import CustomCheckbox from './CustomCheckbox'
import { CheckboxItem, Container, EmailInput } from './styles'
import { defaultMarketingPreferences } from './constants'
import { MarketingPreferences } from './types'
import { selectUser } from '../../modules/user/selectors'
import { theseus } from '../../api/theseus'
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'
import { ThemedButton } from '../../shared_components/ThemedButton'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import AnimatedLoader from '../../shared_components/AnimatedLoader'
import styled from 'styled-components'


import { useRefetchUpdateUser } from '../TwitterAuth'

import {
  H2,
  Span,
} from '../../shared_components/Library/Typeography'
import ConnectWalletButton from '../../shared_components/Library/Buttons/ConnectWalletButton'
import { BackButton } from '../../shared_components/Page'
import { useShouldShowForThreshold } from '../../theme'

const Communications = () => {
  const { isLoggedIn, isAuthenticating } = useIsLoggedIn()
  const isDarkTheme = useSelector(selectIsDarkThemeState)
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  const dispatch = useDispatch()

  const [marketingPreferences, setMarketingPreferences] =
    useState<MarketingPreferences>({ ...defaultMarketingPreferences })

  useEffect(() => {
    dispatch(setHeaderBgUrl(`/hero-cover-labs.png`))
    dispatch(setShowHeaderGradient(false))
    return () => {
      dispatch(setHeaderBgUrl(''))
    }
  }, [isDarkTheme, dispatch])

  const user = useSelector(selectUser)

  const refetchUpdateUser = useRefetchUpdateUser()
  const location = useLocation()

  const [updateCount, setUpdateCount] = useState(0)

  const initialMarketingPreferences = useMemo(() => {
    const preferences = {}
    Object.keys(defaultMarketingPreferences).forEach((key) => {
      preferences[key] = {
        ...defaultMarketingPreferences[key],
        isActive: !!user?.[key],
      }
    })
    return preferences
  }, [user, updateCount])

  const hasUpdatedMarketingPreferences = Object.keys(marketingPreferences).some(
    (key) =>
      marketingPreferences[key].isActive !==
      initialMarketingPreferences[key].isActive,
  )

  useEffect(() => {
    const hash = location.hash
    if (!hash) return

    const element = document.getElementById(hash.substring(1))

    if (!element) return

    element.scrollIntoView({ behavior: 'smooth' })
  }, [location])

  useEffect(() => {
    const preferences = {}
    Object.keys(defaultMarketingPreferences).forEach((key) => {
      preferences[key] = {
        ...defaultMarketingPreferences[key],
        isActive: !!user?.[key],
      }
    })
    setMarketingPreferences(preferences)
  }, [user])

  useEffect(() => {
    refetchUpdateUser()
  }, [])

  const updateCommunicationPreferences = useCallback(async () => {
    try {
      const preferences = {}
      Object.keys(marketingPreferences).forEach((key) => {
        preferences[key] = marketingPreferences[key].isActive
      })
      await theseus.put('/user/communication-preferences', {
        preferences,
      })
      refetchUpdateUser()
      toast.success('Your preferences have been saved')
    } catch (e) {
      console.error(e)
      toast.error('Something went wrong. Please try again.')
    }
  }, [marketingPreferences, refetchUpdateUser])

  if (!isLoggedIn && !isAuthenticating) return <LogoutSelfDestruct />

  if (!user) {
    return (
      <ConnectWalletButton />
    )
  }
  
  return (
    <Box jc>
      <Box dc g={32} mw="628px">
        <H2 s="XL" fw="semibold" c="text-primary">
          Communication
        </H2>
        <Span s="L" c="text-secondary">
          Setting up and managing your communication preferences
        </Span>
        <Box padB={40} dc g={8}>
          <Box fw>
            {Object.keys(marketingPreferences).map((key) => {
              const { label } = marketingPreferences[key]
              const toggle = () =>
                setMarketingPreferences((p) => ({
                  ...p,
                  [key]: { ...p[key], isActive: !p[key].isActive },
                }))
              const isSelected = marketingPreferences[key].isActive
              return (
                <CheckboxItem
                  key={key}
                  onClick={toggle}
                  isSelected={isSelected}
                >
                  <CustomCheckbox isSelected={isSelected} />
                  <p>{label}</p>
                </CheckboxItem>
              )
            })}
          </Box>
          <Box
            jfs
            className="mobile-top-margin"
            marginTop={32}
            marginBottom={32}
          >
            <ThemedButton
              onClick={updateCommunicationPreferences}
              $styleType="primary"
              disabled={!hasUpdatedMarketingPreferences}
            >
              Save preferences
            </ThemedButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Communications

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

export function isValidEmail(email: string) {
  return emailRegex.test(email)
}

const Outer = styled(Box)`
  min-height: 50vh;
`

function LogoutSelfDestruct() {
  useEffect(() => {
    let timer = setTimeout(() => {
      window.location.href = '/'
      timer = null
    }, 1000)
    return () => {
      clearTimeout(timer)
      timer = null
    }
  }, [])
  return (
    <Container>
      <Outer c>
        <AnimatedLoader onlyWhite />
      </Outer>
    </Container>
  )
}
